import { supabase } from '@/lib/supabaseClient';
import { getCurrentTenantId } from '@/utils/tenantUtils';

/**
 * Pricing models supported by the adder catalog.
 *
 * - quote:    No fixed price; surfaced as "Quote required" on the project.
 * - fixed:    One-time charge in dollars (unit_price). Quantity is not used.
 * - per_unit: Cost = unit_price * quantity, where quantity is a count of units
 *             (panels, outlets, etc.). unit_label is the human-readable noun.
 * - per_watt: Cost = unit_price * project system size in watts. Quantity is
 *             auto-derived from the project (estimated_system_size in kW * 1000).
 * - per_foot: Cost = unit_price * quantity, where quantity is feet entered by
 *             the user.
 */
export type AdderPricingModel =
  | 'quote'
  | 'fixed'
  | 'per_unit'
  | 'per_watt'
  | 'per_foot';

/**
 * One catalog entry, as stored in the `adders` table.
 */
export interface Adder {
  id: number;
  tenant_id: string | null;
  name: string;
  category: string | null;
  pricing_model: AdderPricingModel;
  unit_price: number | null;
  unit_label: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Per-project snapshot of an adder, stored as a JSONB entry on `projects.adders`.
 * Snapshots preserve historical pricing — later catalog edits do NOT change cost
 * on existing projects.
 */
export interface ProjectAdder {
  /** Catalog adder ID. `null` for custom project-scoped adders. */
  adder_id: number | null;
  name_snapshot: string;
  category_snapshot: string | null;
  pricing_model_snapshot: AdderPricingModel;
  unit_price_snapshot: number | null;
  unit_label_snapshot: string | null;
  /** null for `fixed` and `quote` models. */
  quantity: number | null;
  /** null for `quote` model. */
  computed_cost: number | null;
  /** True for one-off adders created directly on this project. */
  is_custom?: boolean;
}

export interface CreateAdderInput {
  name: string;
  category?: string | null;
  pricing_model: AdderPricingModel;
  unit_price?: number | null;
  unit_label?: string | null;
  is_active?: boolean;
}

export type UpdateAdderInput = Partial<CreateAdderInput>;

/**
 * Compute the cost for a single project adder given a quantity and (for
 * per_watt models) the project's system size in watts.
 *
 * Returns null when the model is `quote` (no price determinable).
 */
export function computeAdderCost(
  pricingModel: AdderPricingModel,
  unitPrice: number | null,
  quantity: number | null,
  systemSizeWatts?: number | null,
): number | null {
  if (pricingModel === 'quote') return null;
  if (unitPrice == null) return null;

  switch (pricingModel) {
    case 'fixed':
      return Number(unitPrice);
    case 'per_unit':
    case 'per_foot':
      if (quantity == null || quantity <= 0) return 0;
      return Number(unitPrice) * Number(quantity);
    case 'per_watt': {
      const watts = systemSizeWatts ?? 0;
      if (watts <= 0) return 0;
      return Number(unitPrice) * Number(watts);
    }
    default:
      return null;
  }
}

/**
 * Build a fresh ProjectAdder snapshot from a catalog Adder.
 * Cost is computed at the moment of selection so it appears immediately.
 */
export function snapshotAdder(
  adder: Adder,
  quantity: number | null,
  systemSizeWatts?: number | null,
): ProjectAdder {
  return {
    adder_id: adder.id,
    name_snapshot: adder.name,
    category_snapshot: adder.category,
    pricing_model_snapshot: adder.pricing_model,
    unit_price_snapshot: adder.unit_price,
    unit_label_snapshot: adder.unit_label,
    quantity,
    computed_cost: computeAdderCost(
      adder.pricing_model,
      adder.unit_price,
      quantity,
      systemSizeWatts,
    ),
  };
}

export const addersService = {
  /**
   * List all active catalog adders visible to the current tenant. Includes
   * the tenant's own rows plus any legacy global rows (tenant_id IS NULL).
   * Sorted by category then name for stable display.
   */
  async listAdders(options?: { includeInactive?: boolean }): Promise<Adder[]> {
    const tenantId = await getCurrentTenantId();
    if (!tenantId) throw new Error('No tenant ID found');

    let query = supabase
      .from('adders')
      .select('*')
      .or(`tenant_id.is.null,tenant_id.eq.${tenantId}`)
      .order('category', { ascending: true, nullsFirst: false })
      .order('name', { ascending: true });

    if (!options?.includeInactive) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Failed to list adders:', error);
      throw new Error('Unable to load adders. Please try again.');
    }
    return (data || []) as Adder[];
  },

  /**
   * Distinct list of categories already in use for the current tenant. Used
   * by the admin form to autocomplete the category field.
   */
  async listCategories(): Promise<string[]> {
    const tenantId = await getCurrentTenantId();
    if (!tenantId) throw new Error('No tenant ID found');

    const { data, error } = await supabase
      .from('adders')
      .select('category')
      .or(`tenant_id.is.null,tenant_id.eq.${tenantId}`)
      .not('category', 'is', null);

    if (error) {
      console.error('Failed to list adder categories:', error);
      throw new Error('Unable to load adder categories.');
    }

    const seen = new Set<string>();
    for (const row of data || []) {
      const c = (row as { category: string | null }).category;
      if (c && c.trim()) seen.add(c.trim());
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  },

  async createAdder(input: CreateAdderInput): Promise<Adder> {
    const tenantId = await getCurrentTenantId();
    if (!tenantId) throw new Error('No tenant ID found');

    const { data: userData } = await supabase.auth.getUser();

    const payload = {
      tenant_id: tenantId,
      name: input.name.trim(),
      category: input.category?.trim() || null,
      pricing_model: input.pricing_model,
      unit_price:
        input.pricing_model === 'quote' ? null : (input.unit_price ?? null),
      unit_label:
        input.pricing_model === 'per_unit'
          ? input.unit_label?.trim() || null
          : null,
      is_active: input.is_active ?? true,
      created_by: userData?.user?.id || null,
    };

    const { data, error } = await supabase
      .from('adders')
      .insert(payload)
      .select()
      .single();

    if (error) {
      console.error('Failed to create adder:', error);
      throw new Error('Unable to create adder. Please try again.');
    }
    return data as Adder;
  },

  async updateAdder(id: number, input: UpdateAdderInput): Promise<Adder> {
    const updates: Record<string, unknown> = {};
    if (input.name !== undefined) updates.name = input.name.trim();
    if (input.category !== undefined)
      updates.category = input.category?.trim() || null;
    if (input.pricing_model !== undefined)
      updates.pricing_model = input.pricing_model;
    if (input.unit_price !== undefined) updates.unit_price = input.unit_price;
    if (input.unit_label !== undefined)
      updates.unit_label = input.unit_label?.trim() || null;
    if (input.is_active !== undefined) updates.is_active = input.is_active;

    // Enforce model-specific nulls so the row stays internally consistent
    // even when only pricing_model is changed.
    if (input.pricing_model === 'quote') updates.unit_price = null;
    if (input.pricing_model && input.pricing_model !== 'per_unit')
      updates.unit_label = null;

    const { data, error } = await supabase
      .from('adders')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Failed to update adder:', error);
      throw new Error('Unable to update adder. Please try again.');
    }
    return data as Adder;
  },

  async deleteAdder(id: number): Promise<void> {
    const { error } = await supabase.from('adders').delete().eq('id', id);
    if (error) {
      console.error('Failed to delete adder:', error);
      throw new Error('Unable to delete adder. Please try again.');
    }
  },
};
